// Servicio de recordatorios automáticos
import { collection, query, where, getDocs, updateDoc, doc, getFirestore } from 'firebase/firestore';
import app from '../config/firebase';
import emailService from './emailService';
import dayjs from 'dayjs';

// Inicializar Firestore
const db = getFirestore(app);

class ReminderService {
  constructor() {
    this.isRunning = false;
    this.intervalId = null;
    this.checkInterval = 5 * 60 * 1000; // Revisar cada 5 minutos
    console.log('🔔 ReminderService inicializado');
  }

  // Iniciar el servicio de recordatorios
  start() {
    if (this.isRunning) {
      console.log('⚠️ ReminderService ya está ejecutándose');
      return;
    }

    console.log('🚀 Iniciando ReminderService - revisiones cada 5 minutos');
    this.isRunning = true;
    
    // Ejecutar inmediatamente
    this.checkUpcomingAppointments();
    
    // Configurar intervalo
    this.intervalId = setInterval(() => {
      this.checkUpcomingAppointments();
    }, this.checkInterval);
  }

  // Detener el servicio
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
    console.log('⏹️ ReminderService detenido');
  }

  // Verificar citas próximas y enviar recordatorios
  async checkUpcomingAppointments() {
    try {
      console.log('🔍 Revisando citas para recordatorios...', new Date().toLocaleString());
      
      const ahora = dayjs();
      
      // Buscar citas para hoy que estén entre 55-65 minutos en el futuro
      const fechaHoy = ahora.format('YYYY-MM-DD');
      const horaMinima = ahora.add(55, 'minute').format('HH:mm');
      const horaMaxima = ahora.add(65, 'minute').format('HH:mm');
      
      console.log(`📅 Buscando citas para ${fechaHoy} entre ${horaMinima} y ${horaMaxima}`);
      
      const citasRef = collection(db, 'citas');
      const q = query(
        citasRef,
        where('fecha', '==', fechaHoy),
        where('estado', '!=', 'cancelada')
      );
      
      const querySnapshot = await getDocs(q);
      let citasProcesadas = 0;
      let recordatoriosEnviados = 0;
      
      for (const docSnapshot of querySnapshot.docs) {
        const cita = docSnapshot.data();
        const citaId = docSnapshot.id;
        
        // Verificar si la cita está en el rango de tiempo para recordatorio
        const horaCita = cita.hora.split(' ')[0]; // Extraer solo la hora (ej: "10:00" de "10:00 (Disponible)")
        
        if (horaCita >= horaMinima && horaCita <= horaMaxima) {
          citasProcesadas++;
          
          // Verificar si ya se envió el recordatorio
          if (!cita.recordatorioEnviado && cita.email) {
            console.log(`📧 Enviando recordatorio para ${cita.paciente} (${cita.email}) - Cita: ${horaCita}`);
            
            // Enviar recordatorio
            const emailData = {
              ...cita,
              esRecordatorio: true // Flag para indicar que es un recordatorio
            };
            
            const emailResult = await emailService.sendAppointmentReminder(emailData);
            
            if (emailResult.success) {
              // Marcar como recordatorio enviado
              await updateDoc(doc(db, 'citas', citaId), {
                recordatorioEnviado: true,
                recordatorioEnviadoAt: new Date()
              });
              
              recordatoriosEnviados++;
              console.log(`✅ Recordatorio enviado para ${cita.paciente}`);
            } else {
              console.error(`❌ Error enviando recordatorio para ${cita.paciente}:`, emailResult.message);
            }
          } else if (cita.recordatorioEnviado) {
            console.log(`⏭️ Recordatorio ya enviado para ${cita.paciente}`);
          } else if (!cita.email) {
            console.log(`⚠️ Sin email para ${cita.paciente}`);
          }
        }
      }
      
      if (citasProcesadas > 0) {
        console.log(`📊 Revisión completada: ${citasProcesadas} citas procesadas, ${recordatoriosEnviados} recordatorios enviados`);
      }
      
    } catch (error) {
      console.error('❌ Error verificando citas para recordatorios:', error);
    }
  }

  // Método para probar el servicio manualmente
  async testReminders() {
    console.log('🧪 Probando servicio de recordatorios...');
    await this.checkUpcomingAppointments();
  }

  // Obtener estado del servicio
  getStatus() {
    return {
      isRunning: this.isRunning,
      checkInterval: this.checkInterval,
      nextCheck: this.intervalId ? dayjs().add(this.checkInterval, 'ms').format('HH:mm:ss') : null
    };
  }
}

// Crear instancia única
const reminderService = new ReminderService();

export default reminderService;
